import { Box, Button, Stack } from '@chronosphereio/chrono-ui';
import { randomString, useFeatureFlags } from '@chronosphereio/core';
import { produce } from 'immer';
import { Plus as AddIcon } from 'mdi-material-ui';
import { useState } from 'react';
import {
  ChronosphereDataQueryDefinition,
  FormulaDefinition,
  indexToAlpha,
  QueryDefinition,
  QueryOptionsDefinition,
  SupportedDataType,
} from '@/core';
import { OptionsEditorOnChangeContext, useVizFrameworkConfig } from '../../../model';
import { usePlugin, usePluginRegistry } from '../../../runtime';
import { DataQueryEditorProvider, OnSuggestQueryFn } from './DataQueryEditorProvider';
import { DataQueryInput, DataQueryInputProps } from './DataQueryInput';
import { FormulaInput } from './FormulaInput';
import { QueryOptionsEditor } from './QueryOptionsEditor';

const DEFAULT_QUERY_PLUGIN_TYPE = 'DataQuery';

export interface DataQueryEditorProps {
  dataType?: SupportedDataType;

  queries?: QueryDefinition[];
  onQueriesChange: (queries: QueryDefinition[], ctx?: OptionsEditorOnChangeContext) => void;

  showQueryOptions?: boolean;
  queryOptions?: QueryOptionsDefinition;
  onQueryOptionsChange?: (queryOptions: QueryOptionsDefinition) => void;

  formulas?: FormulaDefinition[];
  onFormulasChange?: (formulas: FormulaDefinition[]) => void;

  /* disableFormulasButton gives the caller the ability to grey out the formula button
   * and prevent the user from adding new formulas.
   * This is useful when graph per query is enabled and formulas may not work correctly.
   */
  disableFormulasButton?: boolean;
  /**
   * Gives the ability to render additional controls for a query
   */
  renderAdditionalControls?: DataQueryInputProps['renderAdditionalControls'];
  /**
   * Gives the ability to override the plugin kind selector for a query
   */
  renderPluginKindSelector?: (kind: string) => React.ReactNode;
  /**
   * Gives the ability to render additional toolbar items after all the queries
   */
  renderAdditionalToolbar?: React.ReactNode;
  /**
   * Gives ability to render additional action buttons next to the hide/delete buttons.
   */
  renderAdditionalActions?: DataQueryInputProps['renderAdditionalActions'];

  onSuggestQuery?: OnSuggestQueryFn;
}

const emptyOnFormulasChange = () => {};

export function DataQueryEditor({
  queries = [],
  onQueriesChange,
  showQueryOptions = false,
  queryOptions,
  onQueryOptionsChange,
  formulas = [],
  onFormulasChange = emptyOnFormulasChange,
  disableFormulasButton = false,
  dataType = 'TimeSeries',
  renderAdditionalControls,
  renderPluginKindSelector,
  renderAdditionalToolbar,
  renderAdditionalActions,
  onSuggestQuery,
}: DataQueryEditorProps) {
  const config = useVizFrameworkConfig();

  const { defaultPluginKinds } = usePluginRegistry();
  const defaultQueryPluginKind = defaultPluginKinds?.[DEFAULT_QUERY_PLUGIN_TYPE] ?? '';

  const { data: defaultDataQueryPlugin } = usePlugin(DEFAULT_QUERY_PLUGIN_TYPE, defaultQueryPluginKind, {
    useErrorBoundary: true,
    enabled: true,
  });

  const { enableVizFrameworkFormulas: enableFormulas } = useFeatureFlags();
  const showFormulas = enableFormulas && dataType !== 'HeatMap';

  // State for dataquery inputs metadata.
  const [visibilityState, setVisibilityState] = useState(
    queries.map(() => {
      return { id: randomString(), collapsed: false };
    })
  );

  /* Query handlers */
  const handleQueryAdd = () => {
    if (!defaultDataQueryPlugin) return;
    onQueriesChange(
      produce(queries, (draft) => {
        const queryDef: QueryDefinition = {
          kind: DEFAULT_QUERY_PLUGIN_TYPE,
          spec: {
            plugin: {
              kind: defaultQueryPluginKind,
              spec: defaultDataQueryPlugin.createInitialOptions({ dataType, config }),
            } as ChronosphereDataQueryDefinition,
          },
        };
        if (draft) {
          draft.push(queryDef);
        } else {
          draft = [...queries, queryDef];
        }
      })
    );
    setVisibilityState((visibilityState) => {
      visibilityState.push({ id: randomString(), collapsed: false });
      return [...visibilityState];
    });
  };

  const handleQueryChange = (index: number, queryDef: QueryDefinition, ctx?: OptionsEditorOnChangeContext) => {
    onQueriesChange(
      produce(queries, (draft) => {
        if (draft) {
          draft[index] = queryDef;
        } else {
          draft = [queryDef];
        }
      }),
      ctx
    );
  };

  const handleQueryDelete = (index: number) => {
    onQueriesChange(
      produce(queries, (draft) => {
        draft.splice(index, 1);
      })
    );
    setVisibilityState((visibilityState) => {
      visibilityState.splice(index, 1);
      return [...visibilityState];
    });
  };

  const handleQueryDuplicate = (index: number) => {
    if (!queries || !queries[index]) return;
    const newIndex = index + 1;
    onQueriesChange(
      produce(queries, (draft) => {
        const queryDef = draft[index];
        if (!queryDef) return;
        draft.splice(newIndex, 0, { ...queryDef });
      })
    );
    setVisibilityState((visibilityState) => {
      visibilityState.splice(newIndex, 0, {
        id: randomString(),
        collapsed: false,
      });
      return [...visibilityState];
    });
  };

  const handleQueryCollapseExpand = (index: number) => {
    setVisibilityState((visibilityState) => {
      const state = visibilityState[index];
      if (state !== undefined) {
        state.collapsed = !state.collapsed;
      }
      return [...visibilityState];
    });
  };

  const handleQueryToggleDisabled = (index: number) => {
    onQueriesChange(
      produce(queries, (draft) => {
        const query = draft[index];
        if (query !== undefined) {
          query.spec.disabled = !(query.spec.disabled || false);
        }
      })
    );
  };

  /* Formula handlers */
  const handleFormulaAdd = () => {
    onFormulasChange(
      produce(formulas, (draft) => {
        const formula: FormulaDefinition = {
          kind: 'Formula',
          spec: {
            expression: '',
          },
        };
        if (draft) {
          draft.push(formula);
        } else {
          draft = [...formulas, formula];
        }
      })
    );
  };

  const handleFormulaChange = (idx: number, formula: FormulaDefinition) => {
    onFormulasChange(
      produce(formulas, (draft) => {
        const f = draft[idx];
        if (f) {
          draft[idx] = formula;
        } else {
          if (!draft) {
            draft = [];
          }
          draft[idx] = formula;
        }
      })
    );
  };

  const handleFormulaDelete = (idx: number) => {
    onFormulasChange(
      produce(formulas, (draft) => {
        if (draft) {
          draft.splice(idx, 1);
        }
      })
    );
  };

  const handleFormulaToggleDisabled = (idx: number) => {
    onFormulasChange(
      produce(formulas, (draft) => {
        const formula = draft[idx];
        if (formula !== undefined) {
          formula.spec.disabled = !formula.spec.disabled;
        }
      })
    );
  };

  // show one query input if queries is empty
  const queryDefinitions: QueryDefinition[] = queries.length
    ? queries
    : [
        {
          kind: 'DataQuery',
          spec: {
            plugin: {
              kind: defaultQueryPluginKind ?? '',
              spec: defaultDataQueryPlugin?.createInitialOptions({ dataType, config }) ?? { query: '' },
            } as ChronosphereDataQueryDefinition,
          },
        },
      ];

  const validFormulaVariables = new Set<string>();
  queryDefinitions.forEach((query, i) => {
    validFormulaVariables.add(indexToAlpha(i).toLowerCase());
  });

  return (
    <DataQueryEditorProvider onSuggestQuery={onSuggestQuery}>
      <Stack spacing={1.5} mb={1}>
        {showQueryOptions && onQueryOptionsChange !== undefined && (
          <QueryOptionsEditor queryOptions={queryOptions} onQueryOptionsChange={onQueryOptionsChange} />
        )}
        {queryDefinitions.map((query: QueryDefinition, i: number) => (
          <DataQueryInput
            // Use the unique identifier as the key
            key={`${visibilityState[i]?.id} + ${visibilityState[i]?.collapsed}`}
            index={i}
            query={query}
            isCollapsed={!!visibilityState[i]?.collapsed}
            onChange={(index, query, ctx) => {
              handleQueryChange(index, query, ctx);
            }}
            onDelete={handleQueryDelete}
            onDuplicate={handleQueryDuplicate}
            onCollapseExpand={handleQueryCollapseExpand}
            onToggleDisabled={handleQueryToggleDisabled}
            dataType={dataType}
            renderAdditionalControls={renderAdditionalControls}
            renderPluginKindSelector={renderPluginKindSelector}
            renderAdditionalActions={renderAdditionalActions}
          />
        ))}
        {showFormulas &&
          formulas.map((formula, idx) => (
            <FormulaInput
              key={`formula-${idx}`}
              onChange={handleFormulaChange}
              onDelete={handleFormulaDelete}
              onToggleDisabled={handleFormulaToggleDisabled}
              formula={formula}
              idx={idx}
              validVariables={validFormulaVariables}
            />
          ))}
        <Stack direction="row" spacing={1} alignItems={'start'} pb={1}>
          <Button variant="secondary" StartIcon={<AddIcon />} onClick={handleQueryAdd}>
            Add query
          </Button>
          {showFormulas && (
            <Button
              variant="secondary"
              StartIcon={<AddIcon />}
              disabled={disableFormulasButton}
              onClick={handleFormulaAdd}
            >
              Add formula
            </Button>
          )}
          {renderAdditionalToolbar && <Box flexGrow={1}>{renderAdditionalToolbar}</Box>}
        </Stack>
      </Stack>
    </DataQueryEditorProvider>
  );
}
